import { isConversationTurnStart } from '../../shared/conversationTurns';
import type { CoworkMessage } from '../types/cowork';

export type ToolGroupItem = {
  type: 'tool_group';
  toolUse: CoworkMessage;
  toolResult?: CoworkMessage | null;
};

export type DisplayItem =
  | { type: 'message'; message: CoworkMessage }
  | ToolGroupItem;

export type AssistantTurnItem =
  | { type: 'steering'; message: CoworkMessage }
  | { type: 'assistant'; message: CoworkMessage }
  | { type: 'system'; message: CoworkMessage }
  | { type: 'tool_group'; group: ToolGroupItem }
  | { type: 'tool_result'; message: CoworkMessage };

export type ConversationTurn = {
  id: string;
  userMessage: CoworkMessage | null;
  assistantItems: AssistantTurnItem[];
};

/** 按显式调用 ID 配对工具结果，保留孤立结果和原始消息顺序。 */
export const buildDisplayItems = (messages: CoworkMessage[]): DisplayItem[] => {
  const items: DisplayItem[] = [];
  const groupsByToolUseId = new Map<string, ToolGroupItem>();
  let pendingAdjacentGroup: ToolGroupItem | null = null;

  for (const message of messages) {
    if (message.type === 'tool_use') {
      const group: ToolGroupItem = { type: 'tool_group', toolUse: message };
      items.push(group);

      const toolUseId = message.metadata?.toolUseId;
      if (typeof toolUseId === 'string' && toolUseId.trim()) {
        groupsByToolUseId.set(toolUseId, group);
      }
      pendingAdjacentGroup = group;
      continue;
    }

    if (message.type === 'tool_result') {
      let matched = false;
      const toolUseId = message.metadata?.toolUseId;
      if (typeof toolUseId === 'string' && groupsByToolUseId.has(toolUseId)) {
        const group = groupsByToolUseId.get(toolUseId);
        if (group && !group.toolResult) {
          group.toolResult = message;
          matched = true;
        }
      } else if (!(typeof toolUseId === 'string' && toolUseId.trim()) && pendingAdjacentGroup && !pendingAdjacentGroup.toolResult) {
        // 仅缺失 ID 的历史协议可相邻配对；显式不同 ID 不能错归属。
        pendingAdjacentGroup.toolResult = message;
        matched = true;
      }

      pendingAdjacentGroup = null;
      if (!matched) {
        items.push({ type: 'message', message });
      }
      continue;
    }

    pendingAdjacentGroup = null;
    items.push({ type: 'message', message });
  }

  return items;
};

/** 按用户提问分轮；开头的系统消息保留为独立历史轮，不跨轮吞并正文。 */
export const buildConversationTurns = (items: DisplayItem[]): ConversationTurn[] => {
  const turns: ConversationTurn[] = [];
  let currentTurn: ConversationTurn | null = null;
  let orphanIndex = 0;

  const ensureTurn = (): ConversationTurn => {
    if (currentTurn) return currentTurn;
    const orphanTurn: ConversationTurn = {
      id: `orphan-${orphanIndex++}`,
      userMessage: null,
      assistantItems: [],
    };
    turns.push(orphanTurn);
    currentTurn = orphanTurn;
    return orphanTurn;
  };

  for (const item of items) {
    if (item.type === 'message' && isConversationTurnStart(item.message)) {
      currentTurn = {
        id: item.message.id,
        userMessage: item.message,
        assistantItems: [],
      };
      turns.push(currentTurn);
      continue;
    }

    const turn = ensureTurn();
    if (item.type === 'tool_group') {
      turn.assistantItems.push({ type: 'tool_group', group: item });
      continue;
    }

    const message = item.message;
    if (message.type === 'user' && message.metadata?.steered === true) {
      turn.assistantItems.push({ type: 'steering', message });
      continue;
    }
    if (message.type === 'assistant') {
      turn.assistantItems.push({ type: 'assistant', message });
      continue;
    }

    if (message.type === 'system') {
      turn.assistantItems.push({ type: 'system', message });
      continue;
    }

    if (message.type === 'tool_result') {
      turn.assistantItems.push({ type: 'tool_result', message });
      continue;
    }

    if (message.type === 'tool_use') {
      turn.assistantItems.push({
        type: 'tool_group',
        group: {
          type: 'tool_group',
          toolUse: message,
        },
      });
    }
  }

  return turns;
};

/** 含原始索引的展示项，搜索与复制始终使用消息 ID，不采用分组下标。 */
export type IndexedAssistantItem = { item: AssistantTurnItem; index: number };
/** 连续过程段不会跨越正式答复、错误卡或其他常驻内容。 */
export type ActivitySection = { key: string; process: boolean; entries: IndexedAssistantItem[] };
/** 序列从首个工具取得稳定 key，当前工具改变不会重置用户展开状态。 */
export type ToolSequence = { key: string; sequence: boolean; entries: IndexedAssistantItem[] };

/** 提取原始消息 ID，结果消息与调用组保持独立且不受流式正文影响。 */
export function activityMessageId(item: AssistantTurnItem): string {
  return item.type === 'tool_group' ? item.group.toolUse.id : item.message.id;
}

/** 明确投递给用户的工具内容属于答复，不能收进技术执行序列。 */
export function isUserFacingAnswerToolGroup(item: AssistantTurnItem): boolean {
  if (item.type !== 'tool_group') return false;
  const { toolName, toolInput } = item.group.toolUse.metadata ?? {};
  return toolName === 'SendUserMessage'
    || (toolName === 'SendMessage' && (toolInput?.to === 'user' || toolInput?.recipient === 'user'));
}

/** 错误必须常驻可定位；兼容新结构化错误与历史工具错误字段。 */
export function isActivityFailure(item: AssistantTurnItem): boolean {
  const metadata = item.type === 'tool_group' ? item.group.toolResult?.metadata : item.message.metadata;
  return Boolean(metadata?.isError || metadata?.error || metadata?.userError);
}

/** 一次反向遍历计算后续工具，避免每个正文片段重复扫描造成平方复杂度。 */
export function toolGroupsAfter(items: AssistantTurnItem[]): boolean[] {
  const flags = new Array<boolean>(items.length);
  let found = false;
  for (let index = items.length - 1; index >= 0; index--) {
    flags[index] = found;
    if (items[index].type === 'tool_group') found = true;
  }
  return flags;
}

/**
 * 运行中保持单一时间线，结束后只折叠连续的过程段；正式答复和失败始终保留原位。
 * @param items 已完成可见性过滤的轮次条目。
 * @param running 当前轮是否仍执行，流式增量不移动既有条目到其他分区。
 */
export function buildActivitySections(items: AssistantTurnItem[], running: boolean): ActivitySection[] {
  const after = toolGroupsAfter(items);
  const sections: ActivitySection[] = [];
  items.forEach((item, index) => {
    const process = !running && !isActivityFailure(item) && !isUserFacingAnswerToolGroup(item)
      && (item.type === 'tool_group' || item.type === 'tool_result'
        || (item.type === 'assistant' && (item.message.metadata?.isThinking === true
          || Boolean(item.message.metadata?.parentToolUseId) || after[index])));
    const previous = sections[sections.length - 1];
    if (previous?.process === process) previous.entries.push({ item, index });
    else sections.push({ key: activityMessageId(item), process, entries: [{ item, index }] });
  });
  return sections;
}

/**
 * 合并连续工具及其间说明；尾部正文、错误、面向用户的投递都切断序列。
 * 调用方按子代理作用域分别传入 entries，禁止跨子代理拼接。
 */
export function buildToolSequences(entries: IndexedAssistantItem[]): ToolSequence[] {
  const groups: ToolSequence[] = [];
  let pending: ToolSequence | undefined;
  let bridge: IndexedAssistantItem[] = [];
  /** 尚无后续工具时，缓冲文本保持独立展示，避免吞掉流式尾部。 */
  const flushBridge = (): void => {
    for (const entry of bridge) groups.push({ key: activityMessageId(entry.item), sequence: false, entries: [entry] });
    bridge = [];
  };
  for (const entry of entries) {
    const { item } = entry;
    if (pending && item.type === 'assistant' && !isActivityFailure(item)) {
      bridge.push(entry);
      continue;
    }
    if (item.type === 'tool_group' && !isUserFacingAnswerToolGroup(item) && !isActivityFailure(item)) {
      if (pending) {
        pending.entries.push(...bridge, entry);
        bridge = [];
        pending.sequence = true;
      } else {
        pending = { key: activityMessageId(item), sequence: false, entries: [entry] };
        groups.push(pending);
      }
    } else {
      flushBridge();
      pending = undefined;
      groups.push({ key: activityMessageId(item), sequence: false, entries: [entry] });
    }
  }
  flushBridge();
  return groups;
}

/**
 * 子 Agent 归属:消息若带 parentToolUseId(服务端从 SDK agent_progress 的
 * parent_tool_use_id 透传),说明它是某次 Agent 调用的**内部执行过程**,
 * 渲染时聚合成独立区域与主循环步骤分层 —— 否则并行多子 Agent 的工具调用
 * 平铺混排,分不清哪一步是谁做的。
 */
const getMessageSubagentScope = (
  message: CoworkMessage | null | undefined,
): { parentToolUseId: string; label: string } | null => {
  const meta = message?.metadata;
  const parentToolUseId = meta?.parentToolUseId;
  if (typeof parentToolUseId !== 'string' || !parentToolUseId) return null;
  const label = typeof meta?.subagentLabel === 'string' && meta.subagentLabel
    ? meta.subagentLabel
    : '子任务';
  return { parentToolUseId, label };
};

/** 工具以调用消息的作用域为准，普通消息使用自身的子代理标识。 */
const getItemSubagentScope = (
  item: AssistantTurnItem,
): { parentToolUseId: string; label: string } | null => (
  item.type === 'tool_group'
    ? getMessageSubagentScope(item.group.toolUse)
    : getMessageSubagentScope(item.message)
);

/**
 * 渲染计划:把同一子 Agent 的消息聚合成独立区域,并行子 Agent 交错到达的
 * 步骤各归各的时间线(区域锚定在该子 Agent 首条消息出现的位置)。
 */
type SubagentRenderEntry =
  | { kind: 'item'; item: AssistantTurnItem; index: number }
  | {
      kind: 'subagent';
      parentToolUseId: string;
      label: string;
      /** 派生它的 Agent 调用是否已返回结果(= 子 Agent 已结束)。 */
      completed: boolean;
      entries: Array<{ item: AssistantTurnItem; index: number }>;
    };

/** 在当前展示分区内组织子代理条目，保留原始索引用于搜索和复制。 */
export const buildSubagentRenderPlan = (items: IndexedAssistantItem[]): SubagentRenderEntry[] => {
  // 先扫出 Agent 调用的在场性与完成态 —— 其子区域据此决定默认展开/收起。
  // 父 Agent 的 tool_group 不在本窗口(历史截断)时按已完成处理:历史窗口里的
  // 子 Agent 不可能还在跑,否则区域会永久显示「运行中」误导用户。
  // 后台派生(run_in_background)的 Agent 会**立即**拿到一条
  // "Async agent launched..." 占位 tool_result —— 它表示「已启动」而非「已完成」,
  // 不能据此把区域卡片标成已完成并收起(子 Agent 实际还在后台跑)。
  const isAsyncLaunchPlaceholder = (msg: CoworkMessage | null | undefined): boolean => {
    const content = typeof msg?.content === 'string' ? msg.content : '';
    return content.includes('Async agent launched') || content.includes('async_launched');
  };

  const presentAgentIds = new Set<string>();
  const completedAgentIds = new Set<string>();
  for (const { item } of items) {
    if (item.type !== 'tool_group') continue;
    const meta = item.group.toolUse.metadata;
    if (meta?.toolName === 'Agent' && typeof meta.toolUseId === 'string') {
      presentAgentIds.add(meta.toolUseId);
      if (item.group.toolResult && !isAsyncLaunchPlaceholder(item.group.toolResult)) {
        completedAgentIds.add(meta.toolUseId);
      }
    }
  }

  const plan: SubagentRenderEntry[] = [];
  const sections = new Map<string, Extract<SubagentRenderEntry, { kind: 'subagent' }>>();
  items.forEach(({ item, index }) => {
    const scope = getItemSubagentScope(item);
    if (!scope) {
      plan.push({ kind: 'item', item, index });
      return;
    }
    let section = sections.get(scope.parentToolUseId);
    if (!section) {
      section = {
        kind: 'subagent',
        parentToolUseId: scope.parentToolUseId,
        label: scope.label,
        completed: completedAgentIds.has(scope.parentToolUseId)
          || !presentAgentIds.has(scope.parentToolUseId),
        entries: [],
      };
      sections.set(scope.parentToolUseId, section);
      plan.push(section);
    }
    section.entries.push({ item, index });
  });
  return plan;
};
